import { TableHeader, default as Table } from "../shared/Table";
import TabPosition from "../shared/TabPosition";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClientTableProvider from "../client/providers/ClientTableProvider";
import ClientTableTotal from "../client/components/ClientTableTotal";
import { TranslationService } from "../../services/TranslationService";
import { useHistory, useLocation } from "react-router-dom";
import {
  SegmentListResponse,
  SegmentStatus
} from "../../entities/segment/SegmentListResponse";
import QueryString from "qs";
import DashboardSales from "../client/components/DashboardSales";
import CompanyService from "../../services/CompanyService";
import FloatingPanelService from "../shared/FloatingPanel";
import EditSegment from "./EditSegment";
import SegmentService from "../../services/SegmentService";
import { useEndpointData } from "../../utils/TableUtils";
import Loading from "../shared/components/Loading";
import ErrorMessage from "../shared/components/Error";
import PriorityService from "../../services/PriorityService";
import { getEnumArray } from "../../utils/EnumUtils";
import {
  CriterionModel,
  CriterionType,
  Operator
} from "./providers/CriteriaContext";
import TooltipComponent from "../shared/TooltipComponent";
import { Switch } from "../../utils/Utils";
import BreadcrumbHistory from "../shared/BreadcrumbHistory";
import { formatNumber } from "../../utils/FormatUtils";
import SegmentClientTableItem from "../client/components/SegmentClientTableItem";
import { Helmet } from "react-helmet-async";
import { SafeHtml } from "../../components/SafeHtml";

export type SegmentClientListHistory = {
  segment: SegmentListResponse.Segment | undefined;
  folders: SegmentListResponse | undefined;
};
const SegmentClientList: React.FC = () => {
  const { state, search } = useLocation<SegmentClientListHistory>();
  const history = useHistory();
  const transService = TranslationService.translate;
  const definitions = useMemo(
    () => SegmentService.getCriteria(transService),
    [transService]
  );
  const activities = useMemo(
    () =>
      CompanyService.getActivityTypes().map((x) => ({
        label: x.ActivityTypeName,
        value: x.ActivityTypeID.toString()
      })),
    []
  );
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const id = state?.segment
    ? state.segment.SegmentID
    : Number(
        QueryString.parse(search, {
          ignoreQueryPrefix: true,
          parameterLimit: 3
        })["segmentid"]
      );
  if (!Number.isInteger(id)) {
    window.location.href = `/${TranslationService.currentLanguage}/segment`;
  }
  const segmentPromise = useCallback(() => SegmentService.get(id), [id]);
  const foldersPromise = useCallback(
    () =>
      state?.folders?.list
        ? Promise.resolve(state.folders)
        : SegmentService.getList(),
    [state?.folders]
  );

  useEffect(() => {
    if (state?.folders) {
      history.replace(history.location.pathname + history.location.search, {
        segment: state?.segment
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    loading: loadingSegment,
    error: errorSegment,
    response,
    reload
  } = useEndpointData(segmentPromise);
  const foldersResponse = useEndpointData(foldersPromise);

  const segmentFromList = useMemo(() => {
    if (
      foldersResponse.loading ||
      loadingSegment ||
      errorSegment ||
      foldersResponse.error ||
      foldersResponse?.response === undefined ||
      response === undefined
    ) {
      return undefined;
    }
    return foldersResponse.response.list
      .flatMap((x) => x.items)
      .find((y) => y.SegmentID === response.item.SegmentID);
  }, [
    errorSegment,
    foldersResponse.error,
    foldersResponse.loading,
    foldersResponse.response,
    loadingSegment,
    response
  ]);
  const resetSegment = () => {
    if (state?.folders) {
      state.folders = undefined;
    }
    reload();
    foldersResponse.reload();
  };
  const pills = useMemo(() => {
    if (
      loadingSegment ||
      errorSegment ||
      response === undefined ||
      response?.item === undefined
    ) {
      return [];
    }
    const operators = getEnumArray(Operator);

    // Función auxiliar para obtener el texto del operador de forma segura
    const getOperatorText = (operatorValue: number): string => {
      // IMPORTANTE: Verificar Dynamic PRIMERO porque Dynamic e Includes comparten el valor 9
      // y getEnumArray solo incluye uno de ellos en el array
      if (operatorValue === Operator.Dynamic) {
        return (
          TranslationService.translate.Dynamic || "Dinámico"
        ).toLowerCase();
      }
      // Verificar Empty y NotEmpty directamente para asegurar el texto correcto
      if (operatorValue === Operator.Empty) {
        return TranslationService.translate.Empty?.toLowerCase() || "en blanco";
      }
      if (operatorValue === Operator.NotEmpty) {
        return (
          TranslationService.translate.NotEmpty?.toLowerCase() || "no vacío"
        );
      }
      const operator = operators.find((op) => op.value === operatorValue);
      if (operator) {
        return operator.text.toLowerCase();
      }
      return "";
    };

    // Ordenar los Criteria por el campo Order antes de procesarlos
    const sortedCriteria = [...response.item.Criteria].sort((a, b) => {
      const orderA = a.Order ?? 0;
      const orderB = b.Order ?? 0;
      return orderA - orderB;
    });

    type PillItem = {
      type: "separator" | "criterion";
      content: string;
      key: string;
    };
    const pillsArray: PillItem[] = [];

    sortedCriteria.forEach((x, index) => {
      // Agregar separador OR antes de cada criterio (excepto el primero)
      // Los AND no se muestran, solo los OR con salto de línea
      if (index > 0 && x.ConditionOR) {
        const orText = TranslationService.translate.Or || "OR";
        pillsArray.push({
          type: "separator",
          content: orText,
          key: `separator-${index}-${x.CriterionID}`
        });
      }

      const def = definitions.criterionList.find((y) => y.id === x.Field);
      if (def === undefined) {
        return;
      }
      const result = [def.name];
      const criterionIsNumber = [6, 19, 7, 20].includes(def?.id);

      const unit =
        Switch(
          [def.id === 31, "%"],
          [
            CriterionType.Number_Amount === def.type && !criterionIsNumber,
            " " + CompanyService.GetDefaultCurrencySymbol()
          ],
          [
            CriterionType.Number_Amount === def.type && criterionIsNumber,
            " " + TranslationService.translate.Invoices.toLowerCase()
          ],
          [
            CriterionType.Date === def.type,
            " " + TranslationService.translate.Days
          ]
        ) ?? "";
      const isNotCurrency =
        unit.trim() === TranslationService.translate.Days ||
        unit.trim() === TranslationService.translate.Invoices.toLowerCase() ||
        unit.trim() === "%";
      const data: CriterionModel = x;
      if (
        ![
          CriterionType.Client,
          CriterionType.Invoice,
          CriterionType.List
        ].includes(def?.type)
      ) {
        const operatorText = getOperatorText(data.Operator);
        if (operatorText) {
          result.push(operatorText);
        }
        // No mostrar el valor cuando el operador es Empty o NotEmpty
        if (
          data.Operator !== Operator.Empty &&
          data.Operator !== Operator.NotEmpty
        ) {
          if (CriterionType.ActivityType === def.type) {
            result.push(
              "<b>" +
                data.Value +
                " " +
                TranslationService.translate.Days +
                "</b>"
            );
            if (data.ActivityTypes && data.ActivityTypes.trim().length > 0) {
              const operator =
                data.ActivityTypes[0] === "!"
                  ? Operator.NotEqual
                  : Operator.Equal;
              result.push(
                " - " +
                  TranslationService.translate.Activity +
                  " " +
                  getOperatorText(operator)
              );
              const activityLabels = data.ActivityTypes.split(",")
                .map((y) => y.replace("!", "").trim())
                .filter((y) => y.length > 0)
                .map((y) => activities.find((z) => z.value === y)?.label)
                .filter((x) => x)
                .join(", ");
              if (activityLabels) {
                result.push('"' + activityLabels + '"');
              }
            }
          } else {
            result.push(
              "<b>" +
                (isNotCurrency
                  ? data.Value
                  : unit + " " + formatNumber(parseFloat(data.Value))) +
                "</b>"
            );
          }
        }
        pillsArray.push({
          type: "criterion",
          content: result.join(" "),
          key: `criterion-${index}-${x.CriterionID}`
        });
        return;
      }
      if (def?.type !== CriterionType.List) {
        const fields =
          def.type === CriterionType.Client
            ? definitions.clientDefs
            : definitions.invoiceDefs;
        const field = fields.find(
          (x) => x.AdditionalDefinitionID === data.Data
        )!;
        field?.Name && result.push('"' + field?.Name + '"');
        if (field?.type !== CriterionType.List) {
          if (data.Operator >= 0) {
            const operatorText = getOperatorText(data.Operator);
            if (operatorText) {
              result.push(operatorText);
            }
          }
          // No mostrar el valor cuando el operador es Empty o NotEmpty
          if (
            data.Operator !== Operator.Empty &&
            data.Operator !== Operator.NotEmpty
          ) {
            result.push(data.Value + unit);
          }
          pillsArray.push({
            type: "criterion",
            content: result.join(" "),
            key: `criterion-${index}-${x.CriterionID}`
          });
          return;
        }
        const operatorText = getOperatorText(data.Operator);
        if (operatorText) {
          result.push(operatorText);
        }
        if (
          data.Operator !== Operator.Empty &&
          data.Operator !== Operator.Dynamic
        ) {
          // TODO: Validar data value que viene como String en caso de algunos segmentos
          result.push(
            '<b> "' +
              (data?.IDs && data.IDs?.length > 0 ? data.IDs : data?.Value)
                .split(",")
                .map(
                  (x) =>
                    field.AdditionalDefinitionItems.find(
                      (y) => y.AdditionalDefinitionItemID.toString() === x
                    )?.Value || x
                )
                .join(", ") +
              '"</b>'
          );
        }
      }
      pillsArray.push({
        type: "criterion",
        content: result.join(" "),
        key: `criterion-${index}-${x.CriterionID}`
      });
    });

    return pillsArray;
  }, [
    activities,
    definitions.clientDefs,
    definitions.criterionList,
    definitions.invoiceDefs,
    errorSegment,
    loadingSegment,
    response
  ]);
  if (loadingSegment || response === undefined) {
    return <Loading />;
  }
  if (errorSegment) {
    return (
      <ErrorMessage
        onRefresh={resetSegment}
        message={TranslationService.translate.ErrorLoadingList}
      />
    );
  }
  const onEditSegment = () =>
    FloatingPanelService.showPanel({
      children: (
        <EditSegment
          id={id}
          folders={foldersResponse.response}
          reload={resetSegment}
          onDeleteEvent={() =>
            history.replace(`/${TranslationService.currentLanguage}/segment`)
          }
        />
      ),
      title: TranslationService.translate.EditCustomersSegment,
      width: 1020,
      height: 700
    });
  const data = response.item;

  const tableHead: TableHeader[] = [
    new TableHeader(
      "name",
      TranslationService.translate.Client,
      false,
      true,
      " w-155px "
    ),
    new TableHeader(
      "cant",
      TranslationService.translate.Invoices,
      true,
      true,
      " w-105px "
    ),
    new TableHeader(
      "amount",
      TranslationService.translate.Amount,
      true,
      true,
      " w-105px "
    ),
    new TableHeader("due", "", false, false, " w-105px "),
    new TableHeader(
      "lastlog",
      TranslationService.translate.LastActivity,
      false,
      true,
      " w-155px "
    )
  ];
  if (!CompanyService.getCompanyAuth()?.HidePriority) {
    tableHead.push(
      new TableHeader(
        "rank",
        TranslationService.translate.Priority,
        true,
        true,
        " w-105px ",
        TranslationService.translate.PriorityTip
      )
    );
  }
  const priority = (
    <span
      className={
        "ms-1 priority priority-" +
        PriorityService.getRankingIndex(data.Priority)
      }
      style={{ fontSize: 28 }}
    >
      {data.Priority}
    </span>
  );

  return (
    <TabPosition module="segment">
      <Helmet>
        <title>Intiza</title>
      </Helmet>
      <ClientTableProvider segmentId={id}>
        <div className="container-fluid padding">
          <BreadcrumbHistory
            current={{
              name: data?.Name ?? "",
              link: `segment/details?segmentid=${id}`
            }}
            defaultPrevious={{
              name: TranslationService.translate.Segments,
              link: "segment"
            }}
          />
          <div className="d-flex align-items-center justify-content-between mb-3">
            {CompanyService.canDo("editsegment") &&
            (data.SuperSegmentID == null ||
              data.SuperSegmentID === undefined) ? (
              <div
                className="show-child-on-hover pointer"
                onClick={onEditSegment}
              >
                <h3 className="h3 mb-0">
                  {data.Name}{" "}
                  {CompanyService.getSetting("enablesegmentpriority") &&
                    priority}{" "}
                  {!data.Error && data.Status === SegmentStatus.Pending && (
                    <i className="fas fa-spinner-third fa-spin ms-2"></i>
                  )}
                  {data.Error && (
                    <TooltipComponent
                      title={
                        TranslationService.translate.ErrorProcessingSegment
                      }
                    >
                      {" "}
                      <i className="fa-regular fa-circle-exclamation text-danger ms-2"></i>
                    </TooltipComponent>
                  )}
                  <TooltipComponent title={TranslationService.translate.Edit}>
                    <i
                      className="display-when-hovering-parent far fa-pen ms-1"
                      style={{ color: "var(--intiza-blue)", fontSize: 20 }}
                    />
                  </TooltipComponent>
                </h3>
              </div>
            ) : (
              <h3 className="h3 mb-0">
                {data.Name}{" "}
                {CompanyService.getSetting("enablesegmentpriority") &&
                  priority}{" "}
              </h3>
            )}
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setFiltersCollapsed(!filtersCollapsed)}
              style={{
                fontSize: "14px",
                textDecoration: "none",
                whiteSpace: "nowrap"
              }}
            >
              <i
                className={`fa-solid ${filtersCollapsed ? "fa-chevron-down" : "fa-chevron-up"}`}
                style={{ color: "var(--intiza-blue)" }}
              ></i>
              <span className="ms-1" style={{ color: "var(--intiza-blue)" }}>
                {filtersCollapsed
                  ? TranslationService.translate.ShowCriteria
                  : TranslationService.translate.HideCriteria}
              </span>
            </button>
          </div>
          {!filtersCollapsed && (
            <div className="chipsRowFilter">
              {pills.map((x, index) => {
                if (x.type === "separator") {
                  // Para separadores OR, hacer salto de línea antes usando flexbox
                  return (
                    <React.Fragment key={x.key}>
                      <div style={{ width: "100%", flexBasis: "100%" }}></div>
                      <button
                        type="button"
                        className="btn chipItemSeparator"
                        style={{ cursor: "default", userSelect: "text" }}
                      >
                        <SafeHtml html={x.content} />
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    type="button"
                    className="btn chipItem"
                    style={{ cursor: "default", userSelect: "text" }}
                    key={x.key}
                  >
                    <SafeHtml html={x.content} />
                  </button>
                );
              })}
              {data.Error && (
                <TooltipComponent
                  title={TranslationService.translate.ErrorProcessingSegment}
                >
                  {" "}
                  <i className="fa-regular fa-circle-exclamation text-danger ms-2"></i>
                </TooltipComponent>
              )}
            </div>
          )}
          {data.Segment_Action.length > 0 && (
            <>
              <b className="mb-3">
                {TranslationService.translate.ActionsAssociated}:
              </b>
              <div className="chipsRowFilter">
                {data.Segment_Action.map((x) => (
                  <button
                    type="button"
                    className="btn chipItem"
                    style={{ cursor: "default", userSelect: "text" }}
                    key={x.Id}
                  >
                    {x.Value}
                  </button>
                ))}
              </div>
            </>
          )}
          {segmentFromList && (
            <div className="mb-3">
              <DashboardSales
                noDue={
                  segmentFromList.amount && segmentFromList.overdueAmount
                    ? segmentFromList.amount - segmentFromList.overdueAmount
                    : segmentFromList.amount
                }
                overdue={segmentFromList.overdueAmount}
                totalAmount={segmentFromList.amount}
              />
            </div>
          )}
          <Table headers={tableHead} item={SegmentClientTableItem}>
            <ClientTableTotal />
          </Table>
        </div>
      </ClientTableProvider>
    </TabPosition>
  );
};

export default SegmentClientList;
